import React from 'react';
import useAppForm from '@app/site/_form';
import { AbstractButton } from '@app/site/_form/button/AbstractButton';
import Currency from '@app/model/_common/Currency';
import Utilities from '@util/Utilities';
import {
	BULK_ADJUSTMENT_DEFAULT_VALUES,
	BulkAdjustmentType,
	BulkAdjustmentValues,
	isBulkAdjustmentValueValid,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/bulk-adjustment/BulkAdjustment.utilities';

type BulkAdjustmentBarProps = {
	currency: Currency,
	// The text before the controls. Default: "Adjust all rooms".
	label?: string,
	// Apply presses (button or Enter) call this with the mode and the value. Omit it when the bar has no Apply.
	onApply?: (type: BulkAdjustmentType, value: number) => void,
	// Every change of the mode or the value calls this. The Manual Overwrite modal reads the value from here.
	onChange?: (values: BulkAdjustmentValues) => void,
	// Show the Apply button. Default true. The Manual Overwrite modal pushes with its own footer button.
	showApply?: boolean,
	// Enter in the value field applies. Default true. Off in the Manual Overwrite modal: a push must stay explicit.
	applyOnEnter?: boolean,
	// Bump this number to clear the bar (the Reset button of the modal).
	resetEpoch?: number,
	// "row" (default): one line from 640 px up. "stack": the label and the modes on one line, the field below;
	// for a narrow modal such as Manual Overwrite (512 px).
	layout?: 'row' | 'stack',
};

/**
 * The Bulk Adjustment bar: one line, always visible, the same in every Price Edit Modal and in the
 * Manual Overwrite modal. Percent / Amount as button radios, the value field with its unit, Apply.
 * Built from the existing form Components only: RadioField (variant "button"), TextField, AbstractButton.
 */
const parseValue = (value: string): number | undefined => value.trim() === '' ? undefined : Number(value);

const BulkAdjustmentBar: React.FunctionComponent<BulkAdjustmentBarProps> = ({
	currency,
	label = 'Adjust all rooms',
	onApply,
	onChange,
	showApply = true,
	applyOnEnter = true,
	resetEpoch = 0,
	layout = 'row',
}) => {
	const form = useAppForm({
		defaultValues: { type: 'percentage' as BulkAdjustmentType, value: '' },
		onSubmit: async ({ value }) => {
			const adjustment = parseValue(value.value);
			if (!isBulkAdjustmentValueValid(adjustment)) return;
			if (onApply !== undefined) onApply(value.type, adjustment);
		},
	});

	const previousResetEpoch = React.useRef(resetEpoch);
	React.useEffect(() => {
		if (previousResetEpoch.current === resetEpoch) return;
		previousResetEpoch.current = resetEpoch;
		form.reset();
		if (onChange !== undefined) onChange(BULK_ADJUSTMENT_DEFAULT_VALUES);
	}, [form, onChange, resetEpoch]);

	// Enter inside the value field must not submit the form of the modal around the bar (that form pushes prices).
	const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter') return;
		event.preventDefault();
		event.stopPropagation();
		if (applyOnEnter && onApply !== undefined) form.handleSubmit();
	};

	const labelElement = <span className="whitespace-nowrap text-sm font-medium text-gray-700">{label}</span>;
	const modes = <form.AppField name="type"
	listeners={{ onChange: ({ value }) => onChange?.({ type: value, value: parseValue(form.getFieldValue('value')) }) }}>
		{(field) => <div className="flex gap-2">
			<field.RadioField variant="button" value="percentage" label="Percent" extraClassName="flex-1 sm:flex-none" />
			<field.RadioField variant="button" value="fixed" label="Amount" extraClassName="flex-1 sm:flex-none" />
		</div>}
	</form.AppField>;
	const valueField = <form.Subscribe selector={state => state.values.type}>
		{(type) => <div className={layout === 'stack' ? 'relative flex-1' : 'relative flex-1 sm:w-32 sm:flex-none'}>
			<span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-gray-500">
				{type === 'percentage' ? '%' : Utilities.formatCurrency(currency)}
			</span>
			<form.AppField name="value"
			listeners={{ onChange: ({ value }) => onChange?.({ type: form.getFieldValue('type'), value: parseValue(value) }) }}>
				{(field) => <field.TextField placeholder="e.g., 10" extraClassName="pl-8" />}
			</form.AppField>
		</div>}
	</form.Subscribe>;
	const applyButton = showApply ? <form.Subscribe selector={state => state.values.value}>
		{(value) => <AbstractButton
			type="button"
			state="white"
			disabled={!isBulkAdjustmentValueValid(parseValue(value))}
			onClick={() => form.handleSubmit()}>
			Apply
		</AbstractButton>}
	</form.Subscribe> : null;

	return <form.AppForm>
		<div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-4 py-3" role="group" aria-label={label} onKeyDown={onKeyDown}>
			{layout === 'stack'
				? <div className="flex flex-col gap-2">
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">{labelElement}{modes}</div>
					<div className="flex gap-2">{valueField}{applyButton}</div>
				</div>
				: <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
					{labelElement}
					{modes}
					<div className="hidden flex-1 sm:block" />
					<div className="flex gap-2">{valueField}{applyButton}</div>
				</div>}
		</div>
	</form.AppForm>;
};

export default BulkAdjustmentBar;
